/**
 * Methods to contain aggregate values for components UNITS and GMS.
 * Calculates the value for all channels combined ('ALL') given the 
 * rows for all the channels (RETAIL, FBA, MFN)
 */

import { extractColumnKey, extractColumnKeyPrefix } from './composite_keys';

const BENCHMARK_AMOUNT = 'benchmarkAmount';

const COLUMN_CONFIGURATIONS = Object.freeze({
  SUMMATION_COLUMNS: [
    'dailyCPAmount',
    'lastCPAmount',
    'benchmarkAmount',
    'cognosBenchmarkAmount',
    'cognosExcludedAmount',
    'channelExclusionImpactAmount',
    'totalPnL',
    'dailyCPAmountNV',
    'dailyCPAmountAV',
    'dailyCPAmountCognosAV',
    'lastCPAmountNV',
    'lastCPAmountAV',
    'lastCPAmountCognosAV',
    'perUnitNetVarianceDailyCP',
    'perUnitNetVarianceLastCP',
    'perUnitVarianceDailyCP',
    'perUnitVarianceLastCP',
    'perUnitVarianceCognosDailyCP',
    'perUnitVarianceCognosLastCP',
  ],

  ABSOLUTE_VARIANCE: {
    columns: {
      dailyCPAccuracyAV: {
        value: 'dailyCPAmountAV',
        benchmark: 'benchmarkAmount',
      },
      lastCPAccuracyAV: {
        value: 'lastCPAmountAV',
        benchmark: 'benchmarkAmount',
      },
      dailyCPAccuracyCognosAV: {
        value: 'dailyCPAmountCognosAV',
        benchmark: 'cognosBenchmarkAmount',
      },
      lastCPAccuracyCognosAV: {
        value: 'lastCPAmountCognosAV',
        benchmark: 'cognosBenchmarkAmount',
      },
    },
    formula: (rowObject, valueKey, benchmarkKey) => {
      return Math.max(1 - Math.abs(rowObject[valueKey] / rowObject[benchmarkKey]), 0) * 100;
    },
  },

  NET_VARIANCE: {
    columns: {
      dailyCPAccuracyNV: {
        value: 'dailyCPAmount',
        benchmark: 'benchmarkAmount',
      },
      lastCPAccuracyNV: {
        value: 'lastCPAmount',
        benchmark: 'benchmarkAmount',
      },
    },
    formula: (rowObject, valueKey, benchmarkKey) => {
      return Math.max(1 - Math.abs((rowObject[benchmarkKey] - rowObject[valueKey]) / rowObject[benchmarkKey]), 0) * 100;
    },
  },

  DELTA: {
    columns: {
      accuracyDelta: {
        last: 'lastCPAccuracyNV',
        daily: 'dailyCPAccuracyNV',
      },
      accuracyDeltaAV: {
        last: 'lastCPAccuracyAV',
        daily: 'dailyCPAccuracyAV',
      },
      cognosAccuracyDeltaAV: {
        last: 'lastCPAccuracyCognosAV',
        daily: 'dailyCPAccuracyCognosAV',
      },
    },

    formula: (rowObject, benchmarkKey, lastKey, dailyKey) => {
      return rowObject[benchmarkKey] === 0 ? 0 : rowObject[lastKey] - rowObject[dailyKey];
    },
  },
});

export const calculateAggregateRows = (reportRows) => {
  const summationValues = reportRows.reduce((acc, item) => {
    Object.keys(item)
      .filter((k) => COLUMN_CONFIGURATIONS.SUMMATION_COLUMNS.includes(extractColumnKey(k)))
      .forEach((k) => {
        const val = item[k] === 'N/A' ? 0 : item[k];
        acc[k] = (acc[k] || 0) + val;
      });
    return acc;
  }, {});

  const absVarValues = getDerivedValues(COLUMN_CONFIGURATIONS.ABSOLUTE_VARIANCE, summationValues);
  const netVarValues = getDerivedValues(COLUMN_CONFIGURATIONS.NET_VARIANCE, summationValues);

  const mergedValues = { ...summationValues, ...absVarValues, ...netVarValues };

  const deltaValues = getDeltaValues(COLUMN_CONFIGURATIONS.DELTA, mergedValues);

  return { ...mergedValues, ...deltaValues };
};

const getDerivedValues = (columnConfigurations, values) => {
  const { formula, columns } = columnConfigurations;

  return Object.keys(columns).reduce((acc, colKey) => {
    const columnConfig = columns[colKey];
    Object.keys(values)
      .filter((k) => extractColumnKey(k) === columnConfig.value)
      .forEach((k) => {
        const prefix = extractColumnKeyPrefix(k);
        const valueKey = prefix + columnConfig.value;
        const benchmarkKey = prefix + columnConfig.benchmark;
        acc[prefix + colKey] = formula(values, valueKey, benchmarkKey);
      });

    return acc;
  }, {});
};

const getDeltaValues = (columnConfigurations, values) => {
  const { formula, columns } = columnConfigurations;

  return Object.keys(columns).reduce((acc, colKey) => {
    const columnConfig = columns[colKey];
    Object.keys(values)
      .filter((k) => extractColumnKey(k) === columnConfig.last)
      .forEach((k) => {
        const prefix = extractColumnKeyPrefix(k);
        const benchmarkKey = prefix + BENCHMARK_AMOUNT;
        const lastKey = prefix + columnConfig.last;
        const dailyKey = prefix + columnConfig.daily;

        acc[prefix + colKey] = formula(values, benchmarkKey, lastKey, dailyKey);
      });

    return acc;
  }, {});
};



// WEBPACK FOOTER //
// ./src/components/reports/utils/row_aggregations.js